import React from 'react';
import { Box, Card, CardMedia, CardContent, Typography, Chip, Divider, Button } from '@mui/material';

interface OfferMessageCardProps {
  productTitle: string;
  productPrice: number;
  productImageUrl?: string;
  offerPrice: number;
  offerText: string;
  isOwnMessage: boolean;
  offerId?: string;
  onWithdraw?: (offerId: string) => void;
  onEdit?: (offerId: string) => void;
  onReply?: () => void;
  showSellerActions?: boolean;
  onAccept?: (offerId: string) => void;
  onReject?: (offerId: string) => void;
  sellerActionLoading?: boolean;
  actionsDisabled?: boolean;
  isWithdrawn?: boolean;
  isSuperseded?: boolean;
}

const currency = (n: number) => `$${n.toFixed(2)}`;

const OfferMessageCard: React.FC<OfferMessageCardProps> = ({
  productTitle,
  productPrice,
  productImageUrl,
  offerPrice,
  offerText,
  isOwnMessage,
  offerId,
  onWithdraw,
  onEdit,
  onReply,
  showSellerActions = false,
  onAccept,
  onReject,
  sellerActionLoading = false,
  actionsDisabled = false,
  isWithdrawn = false,
  isSuperseded = false,
}) => {
  const savings = productPrice - offerPrice;
  const betterDeal = savings > 0;

  return (
    <Card
      sx={{
        display: 'flex',
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(103, 119, 239, 0.15)',
        border: '1px solid',
        borderColor: (isWithdrawn || isSuperseded) ? 'grey.300' : (isOwnMessage ? 'primary.light' : 'grey.200'),
        opacity: (isWithdrawn || isSuperseded) ? 0.7 : 1,
      }}
    >
      {productImageUrl && (
        <CardMedia
          component="img"
          image={productImageUrl}
          alt={productTitle}
          sx={{ width: { xs: 96, sm: 140 }, objectFit: 'cover' }}
        />
      )}
      <Box sx={{ flex: 1 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip 
              size="small" 
              color={(isWithdrawn || isSuperseded) ? 'default' : 'primary'} 
              label={isWithdrawn ? 'Offer withdrawn' : (isSuperseded ? 'New offer made' : 'Offer')} 
              sx={{ fontWeight: 'bold' }} 
            />
            <Typography variant="subtitle2" color="text.secondary">
              {isWithdrawn
                ? (isOwnMessage ? 'You withdrew this offer' : 'Offer was withdrawn')
                : isSuperseded
                  ? (isOwnMessage ? 'You made a newer offer' : 'A newer offer exists')
                  : (isOwnMessage ? 'You made an offer' : 'New offer received')}
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {productTitle}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Chip
              label={`Offer: ${currency(offerPrice)}`}
              sx={{
                bgcolor: 'success.light',
                color: 'success.dark',
                fontWeight: 700,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Listed price: {currency(productPrice)}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 1.5, textDecoration: isWithdrawn ? 'line-through' : 'none' }}>
            {offerText}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="body2" color={betterDeal ? 'success.main' : 'text.secondary'}>
            {betterDeal
              ? `Potential savings vs listed price: ${currency(savings)}`
              : 'Offer matches or exceeds listed price'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
            {showSellerActions ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  disabled={!offerId || isWithdrawn || isSuperseded || sellerActionLoading || actionsDisabled}
                  onClick={() => offerId && onAccept?.(offerId)}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  disabled={!offerId || isWithdrawn || isSuperseded || sellerActionLoading || actionsDisabled}
                  onClick={() => offerId && onReject?.(offerId)}
                >
                  Reject
                </Button>
              </>
            ) : isOwnMessage ? (
              <>
                <Button 
                  variant="contained" 
                  size="small"
                  disabled={!offerId || isWithdrawn || isSuperseded || actionsDisabled}
                  onClick={() => offerId && onWithdraw?.(offerId)}
                >
                  Withdraw
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  disabled={!offerId || isWithdrawn || isSuperseded || actionsDisabled}
                  onClick={() => offerId && onEdit?.(offerId)}
                >
                  Edit Offer
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => onReply?.()}
                  disabled={isWithdrawn || isSuperseded}
                >
                  Reply
                </Button>
                <Button variant="outlined" size="small" disabled={isWithdrawn || isSuperseded}>View Product</Button>
              </>
            )}
          </Box>
        </CardContent>
      </Box>
    </Card>
  );
};

export default OfferMessageCard;


